import {Employee} from 'client';
import {KNOWLEDGE_ALL_LIMIT} from 'components/search/KnowledgeResultRow';
import {useKnowledgeSearch} from 'components/search/useKnowledgeSearch';
import {isQuestionShaped} from 'helpers/queryShape';
import {useDebouncedValue} from 'hooks/useDebounce';
import {
  isEmployee,
  isNewsPost,
  isTeamSearchResult,
  isWorkstream,
  useGlobalSearch,
} from 'hooks/useEmployee';
import {useMemo} from 'react';

import {SearchRow} from './searchRows';

// One empty list for "nothing yet", so the rows below don't rebuild (and the
// pane chase new objects) on every render while a search is off or loading.
const NONE: never[] = [];

export const personRow = (employee: Employee): SearchRow => ({
  kind: 'person',
  id: `person:${employee.ldap}`,
  person: employee,
});

/**
 * A typed query's results as one list, from today's global and knowledge
 * search: people and teams, then a few knowledge articles and links (right
 * after the last person or team, as today's box puts them), then workstreams
 * and news. A lookup ends with a quiet Ask row; a question starts with it.
 * `loaded` is false while the query is still being fetched.
 */
export const useResultRows = (query: string) => {
  const live = query.trim();
  const debounced = useDebouncedValue(live, 200);
  const isQuestion = isQuestionShaped(live);
  const {data: results = NONE, isFetching} = useGlobalSearch({
    input: debounced,
    filter: 'all',
    shortFallbacks: true,
  });
  const {data: knowledge = NONE, isFetching: isKnowledgeFetching} = useKnowledgeSearch({
    input: debounced,
    enabled: true,
  });

  const rows = useMemo(() => {
    if (!live) return [];
    const found = results.slice(0, isQuestion ? 4 : 7).flatMap((item): SearchRow[] => {
      if (isEmployee(item)) return [personRow(item as Employee)];
      if (isTeamSearchResult(item)) return [{kind: 'team', id: `team:${item.team_id}`, team: item}];
      if (isWorkstream(item)) return [{kind: 'workstream', id: `ws:${item.id}`, workstream: item}];
      if (isNewsPost(item)) return [{kind: 'news', id: `news:${item.id}`, post: item}];
      return [];
    });
    const articles = knowledge
      .slice(0, KNOWLEDGE_ALL_LIMIT)
      .map((result): SearchRow => ({kind: 'knowledge', id: `knowledge:${result.id}`, result}));
    const lastPersonOrTeam = found.findLastIndex(({kind}) => kind === 'person' || kind === 'team');
    const list = [
      ...found.slice(0, lastPersonOrTeam + 1),
      ...articles,
      ...found.slice(lastPersonOrTeam + 1),
    ];
    const ask: SearchRow = {kind: 'ask', id: 'ask', question: live};
    return isQuestion ? [ask, ...list] : [...list, ask];
  }, [live, results, knowledge, isQuestion]);

  const loaded = !isFetching && !isKnowledgeFetching && debounced === live;
  return {live, rows, loaded};
};
